import React, { useEffect, useMemo, useRef, useState } from 'react';

import useTranslation from 'next-translate/useTranslation';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Skeleton,
  Stack,
  Switch,
} from '@mui/material';

import { mutationCreateManySkillsList, queryLanguages, querySkills } from '@itp/prisma-talent/gql';
import { QueryLanguages, QuerySkills, useMutation, usePrivateApollo } from '@itp/talent';
import { Chip } from '@itp/talent/components/core/chip';
import { BodyText, BodyTextBold, HeadingMedium } from '@itp/talent/components/core/typography';

type UpdateProfileData = {
  [key: string]: any;
};

type UpdateProfileDialogProps = {
  hasPartialFields?: boolean;
  isLoading?: boolean;
  loadingStage?: null | string;
  onClose: () => void;
  onConfirm: (data: UpdateProfileData) => void;
  open: boolean;
  updateData: UpdateProfileData;
};

const blocks = [
  {
    fields: ['profileName', 'jobTitle', 'description', 'email', 'location', 'totalExperience'],
    title: 'Main Information',
  },
  {
    fields: ['languages'],
    title: 'Languages',
  },
  {
    fields: ['links'],
    title: 'Links',
  },
  {
    fields: [],
    title: 'Location',
  },
  {
    fields: ['skills'],
    title: 'Skills',
  },
  {
    fields: ['experience'],
    title: 'Experience',
  },
  {
    fields: ['education'],
    title: 'Education',
  },
];

const BlockSkeleton: React.FC = () => (
  <Stack spacing={1}>
    <Skeleton height={20} variant="text" width="60%" />
    <Skeleton height={20} variant="text" width="80%" />
    <Skeleton height={20} variant="text" width="45%" />
  </Stack>
);

export const UpdateProfileDialog: React.FC<UpdateProfileDialogProps> = ({
  hasPartialFields = false,
  isLoading = false,
  loadingStage = null,
  onClose,
  onConfirm,
  open,
  updateData,
}) => {
  const [mappedSkills, setMappedSkills] = useState(updateData.skills || []);

  const isMutationQueued = useRef(false);
  const { t } = useTranslation('profile');
  const isTruthy = (value: any) => {
    return (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0)
    );
  };
  const { data: skillsData, refetch: refetchSkills } = usePrivateApollo<QuerySkills>({
    query: querySkills,
  });

  const { data: languagesData } = usePrivateApollo<QueryLanguages>({
    query: queryLanguages,
    variables: { orderBy: [{ label: 'asc' }] },
  });

  const [createManySkillsList] = useMutation(mutationCreateManySkillsList, {
    messageFail: "Skills can't be created",
    messageSuccess: 'New skills added',
  });

  const skillsMap = useMemo(() => {
    if (!skillsData) return {};
    return skillsData.reduce(
      (map, skill) => {
        map[skill.label.toLowerCase()] = skill.id;
        return map;
      },
      {} as Record<string, string>,
    );
  }, [skillsData]);

  const languagesMap = useMemo(() => {
    if (!languagesData) return {};
    return languagesData.reduce(
      (map, lang) => {
        map[lang.label.toLowerCase()] = lang.id;
        return map;
      },
      {} as Record<string, string>,
    );
  }, [languagesData]);

  useEffect(() => {
    if (isLoading) return;
    if (isMutationQueued.current || Object.keys(skillsMap).length === 0) return;
    if (!updateData?.skills) return;

    const existingSkills = [];
    const newSkillsSet = new Set();

    updateData.skills.forEach((skill) => {
      const labelLower = skill.skill.toLowerCase();
      if (skillsMap[labelLower]) {
        existingSkills.push({ ...skill, id: skillsMap[labelLower] });
      } else {
        newSkillsSet.add(skill.skill);
      }
    });

    const newSkills = Array.from(newSkillsSet).map((skill) => ({ skill: skill }));
    if (newSkills.length > 0) {
      isMutationQueued.current = true;
      const skillsToCreate = newSkills.map((skill) => ({ label: skill.skill }));
      createManySkillsList({
        onCompleted: (data) => {
          refetchSkills().then(() => {
            newSkills.length = 0;
            isMutationQueued.current = false;
          });
        },
        onError: (error) => {
          console.error('Error creating skills:', error);
          isMutationQueued.current = false;
          newSkills.length = 0;
        },
        variables: { data: skillsToCreate },
      });
    } else {
      setMappedSkills(existingSkills);
    }
  }, [skillsMap, isLoading]);

  const mappedLanguages = useMemo(() => {
    if (!updateData.languages) return [];
    return updateData.languages.map((lang: any) => {
      const labelLower = lang.language.toLowerCase();
      const existingId = languagesMap[labelLower];
      return existingId ? { ...lang, id: existingId } : lang;
    });
  }, [updateData.languages, languagesMap]);

  useEffect(() => {
    if (isLoading) return;
    if (!skillsData || !updateData?.skills) return;

    const updatedMappedSkills = updateData.skills.map((skill) => {
      const labelLower = skill.skill.toLowerCase();
      const id = skillsMap[labelLower];
      return { ...skill, id: id || skill.id };
    });
    setMappedSkills(updatedMappedSkills);
  }, [skillsData, isLoading]);

  const [blockToggles, setBlockToggles] = React.useState<{ [key: string]: boolean }>({});

  React.useEffect(() => {
    if (isLoading) return;
    const initialBlockToggles = blocks.reduce(
      (acc, block) => {
        acc[block.title] = true;
        return acc;
      },
      {} as { [key: string]: boolean },
    );
    setBlockToggles(initialBlockToggles);
  }, [updateData, isLoading]);

  const handleBlockToggle = (blockTitle: string) => {
    setBlockToggles((prev) => ({
      ...prev,
      [blockTitle]: !prev[blockTitle],
    }));
  };

  const handleConfirm = () => {
    const dataToSubmit: UpdateProfileData = {};

    blocks.forEach((block) => {
      if (blockToggles[block.title]) {
        block.fields.forEach((field) => {
          if (updateData.hasOwnProperty(field)) {
            if (field === 'skills') {
              dataToSubmit[field] = mappedSkills;
            } else if (field === 'languages') {
              dataToSubmit[field] = mappedLanguages;
            } else {
              dataToSubmit[field] = updateData[field];
            }
          }
        });
      }
    });
    onConfirm(dataToSubmit);
    setMappedSkills(updateData.skills || []);
  };

  const renderFieldValue = (field: string, value: any, blockTitle: string) => {
    if (!isTruthy(value)) return null;

    if (blockTitle === 'Main Information') {
      return (
        <React.Fragment key={field}>
          <BodyTextBold>{t(field)}:</BodyTextBold>
          <BodyText sx={{ mb: 1 }}>{value.toString()}</BodyText>
        </React.Fragment>
      );
    } else if (field === 'languages' && Array.isArray(value)) {
      return (
        <Stack key={field} style={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginBottom: 10 }}>
          {value.map((language: any, idx: number) => (
            <BodyText key={idx}>{language.language}</BodyText>
          ))}
        </Stack>
      );
    } else if (field === 'links' && Array.isArray(value)) {
      return (
        <Stack key={field} style={{ display: 'flex', flexDirection: 'column', marginBottom: 10 }}>
          {value.map((link: any, idx: number) => (
            <BodyText key={idx}>{link.url}</BodyText>
          ))}
        </Stack>
      );
    } else if (field === 'skills' && Array.isArray(value)) {
      return (
        <Stack
          style={{
            columnGap: 16,
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            marginBottom: 10,
            rowGap: 8,
          }}
          key={field}
        >
          {value.map((skill: any, idx: number) => (
            <Chip key={skill.skill + idx} label={skill.skill} size="small" variant="skills" />
          ))}
        </Stack>
      );
    } else if (field === 'experience' && Array.isArray(value)) {
      return (
        <Stack
          key={field}
          style={{ display: 'flex', flexDirection: 'column', gap: 1, marginBottom: 10 }}
        >
          {value.map((exp: any, idx: number) => (
            <Stack key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
              <BodyTextBold>{exp.title}</BodyTextBold>
              <Stack>
                <BodyText>{exp.company}</BodyText>
                <BodyText>
                  {exp.start} - {exp.end}
                </BodyText>
              </Stack>
              <BodyText sx={{ whiteSpace: 'pre-line' }}>{exp.description}</BodyText>
              <Divider sx={{ my: 1 }} />
            </Stack>
          ))}
        </Stack>
      );
    } else if (field === 'education' && Array.isArray(value)) {
      return (
        <Stack
          key={field}
          style={{ display: 'flex', flexDirection: 'column', gap: 1, marginBottom: 10 }}
        >
          {value.map((edu: any, idx: number) => (
            <Stack key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
              <Stack>
                <BodyTextBold sx={{ whiteSpace: 'pre-line' }}>{edu.program}</BodyTextBold>
                <BodyTextBold>{edu.degree}</BodyTextBold>
              </Stack>
              <Stack>
                <BodyText>{edu.school}</BodyText>
                <BodyText>
                  {edu.start} - {edu.end}
                </BodyText>
              </Stack>
              <Divider sx={{ my: 1 }} />
            </Stack>
          ))}
        </Stack>
      );
    } else {
      return (
        <React.Fragment key={field}>
          <BodyTextBold>{field}:</BodyTextBold>
          <BodyText sx={{ mb: 1 }}>{JSON.stringify(value)}</BodyText>
        </React.Fragment>
      );
    }
  };

  const showSkeleton = isLoading && !hasPartialFields;
  const showProgressive = isLoading && hasPartialFields;
  const showFinal = !isLoading;

  return (
    <Dialog fullWidth maxWidth="md" onClose={onClose} open={open}>
      <DialogTitle>{isLoading ? 'Analyzing Your CV...' : 'Update Your Profile'}</DialogTitle>
      <DialogContent>
        {isLoading && loadingStage && (
          <BodyText sx={{ color: 'text.secondary', mb: 2, textAlign: 'center' }}>
            {loadingStage}
          </BodyText>
        )}

        {showSkeleton && (
          <Stack spacing={3}>
            {['Main Information', 'Skills', 'Experience', 'Education'].map((title) => (
              <div key={title}>
                <HeadingMedium variant="h6">{title}</HeadingMedium>
                <Divider sx={{ mb: 2 }} />
                <BlockSkeleton />
              </div>
            ))}
          </Stack>
        )}

        {(showProgressive || showFinal) && (
          <Stack spacing={2}>
            {blocks.map((block) => {
              const hasData = block.fields.some(
                (field) => updateData.hasOwnProperty(field) && isTruthy(updateData[field]),
              );

              if (showFinal && !hasData) return null;

              if (showProgressive && !hasData) {
                return (
                  <div key={block.title}>
                    <HeadingMedium variant="h6">{block.title}</HeadingMedium>
                    <Divider sx={{ mb: 2 }} />
                    <BlockSkeleton />
                  </div>
                );
              }

              return (
                <div key={block.title}>
                  <Stack alignItems="center" direction="row" justifyContent="space-between">
                    <HeadingMedium variant="h6">{block.title}</HeadingMedium>
                    {showFinal && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={blockToggles[block.title] || false}
                            onChange={() => handleBlockToggle(block.title)}
                          />
                        }
                        label={blockToggles[block.title] ? 'Include' : 'Exclude'}
                      />
                    )}
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  {(!showFinal || blockToggles[block.title]) && (
                    <Stack display="flex" flexDirection="column">
                      {block.fields.map((field) => {
                        if (!updateData.hasOwnProperty(field)) return null;
                        return renderFieldValue(field, updateData[field], block.title);
                      })}
                    </Stack>
                  )}
                </div>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        {showFinal && (
          <Button color="primary" onClick={handleConfirm} variant="contained">
            Confirm
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
